import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const CategoryConfigs = ({ cat, onSaved }) => {
  const [list, setList] = useState(cat.configurations || []);
  const [t, setT] = useState("");
  const dirty = JSON.stringify(list) !== JSON.stringify(cat.configurations || []);
  const add = () => {
    const v = t.trim();
    if (!v) return;
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return toast.error(`"${v}" already exists`);
    setList([...list, v]); setT("");
  };
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; setList(n); };
  const save = async () => {
    try { await api.put(`/categories/${cat.id}`, { ...cat, configurations: list }); toast.success(`${cat.name} configurations saved`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="py-5 border-b border-[var(--line)] last:border-0" data-testid={`configs-${cat.id}`}>
      <p className="text-sm font-bold mb-3">{cat.name}</p>
      <div className="space-y-1.5 mb-3">
        {list.length === 0 && <p className="text-xs text-[var(--muted-ink)]">No configurations yet</p>}
        {list.map((c, i) => (
          <div key={c} className="flex items-center gap-2 bg-[var(--paper)] px-3 py-1.5 text-sm" data-testid={`config-option-${cat.id}-${i}`}>
            <span className="flex-1">{c}</span>
            <button disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-20"><ArrowUp size={13} /></button>
            <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-20"><ArrowDown size={13} /></button>
            <button onClick={() => setList(list.filter((_, j) => j !== i))} data-testid={`config-option-remove-${cat.id}-${i}`}><X size={13} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. 2 seater with console" data-testid={`config-option-input-${cat.id}`} />
        <Button variant="outline" onClick={add} data-testid={`config-option-add-${cat.id}`}><Plus size={14} /></Button>
        <Button disabled={!dirty} onClick={save} data-testid={`config-option-save-${cat.id}`}>Save</Button>
      </div>
    </div>
  );
};

export const ConfigurationsCard = () => {
  const [cats, setCats] = useState([]);
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  return (
    <section className="bg-[var(--cream)] p-8 page-shadow lg:col-span-2" data-testid="configurations-card">
      <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--muted-ink)]">Per category</p>
      <h2 className="text-base md:text-lg font-bold mt-1">Standard configurations</h2>
      <p className="text-sm text-[var(--muted-ink)] mt-2">These are the only options in the configuration dropdown when adding a product, in this order.</p>
      <div className="grid md:grid-cols-2 gap-x-10 mt-2">
        {cats.map((c) => <CategoryConfigs key={`${c.id}-${(c.configurations || []).join("|")}`} cat={c} onSaved={load} />)}
      </div>
    </section>
  );
};
